import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check } from "lucide-react";
import clsx from "clsx";
import { Api, type OnboardingTitle, type OnboardingTopic } from "../lib/api.js";

type Phase = "titles" | "topics" | "done";

// 1-10 scale with 7 explicitly disabled — design constraint.
const RATING_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export function Onboarding(): JSX.Element {
  const { id: profileId = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["onboarding", profileId],
    queryFn: () => Api.onboarding.payload(profileId),
    enabled: profileId.length > 0,
  });

  // Walk only the items that aren't rated yet so users can resume.
  const titlesRemaining = useMemo(() => data?.titles.filter((t) => !t.rated) ?? [], [data]);
  const topicsRemaining = useMemo(() => data?.topics.filter((t) => !t.rated) ?? [], [data]);

  const [phase, setPhase] = useState<Phase | null>(null);
  const [titleIdx, setTitleIdx] = useState(0);
  const [topicIdx, setTopicIdx] = useState(0);
  const [doneResult, setDoneResult] = useState<{ tag_weights: Record<string, number>; n_titles_rated: number; n_topics_rated: number } | null>(null);

  // Derive starting phase once data loads.
  if (data && phase === null) {
    if (data.complete) setPhase("done");
    else if (titlesRemaining.length > 0) setPhase("titles");
    else if (topicsRemaining.length > 0) setPhase("topics");
    else setPhase("done");
  }

  const rateTitle = useMutation({
    mutationFn: (vars: { title_id: string; seen: boolean; rating: number | null }) =>
      Api.onboarding.rateTitle(profileId, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["onboarding", profileId] }),
  });

  const rateTopic = useMutation({
    mutationFn: (vars: { topic: string; rating: number }) =>
      Api.onboarding.rateTopic(profileId, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["onboarding", profileId] }),
  });

  const complete = useMutation({
    mutationFn: () => Api.onboarding.complete(profileId),
    onSuccess: (res) => {
      setDoneResult(res.preview);
      setPhase("done");
      void qc.invalidateQueries({ queryKey: ["profiles"] });
      void qc.invalidateQueries({ queryKey: ["onboarding", profileId] });
    },
  });

  const titleProgress = useMemo(() => {
    if (!data) return { done: 0, total: 0 };
    const total = data.titles.length;
    return { done: total - titlesRemaining.length, total };
  }, [data, titlesRemaining]);

  const topicProgress = useMemo(() => {
    if (!data) return { done: 0, total: 0 };
    const total = data.topics.length;
    return { done: total - topicsRemaining.length, total };
  }, [data, topicsRemaining]);

  if (isLoading || !data || phase === null) {
    return <div className="px-10 py-10"><div className="skeleton h-64" /></div>;
  }

  const currentTitle: OnboardingTitle | null = titlesRemaining[titleIdx] ?? null;
  const currentTopic: OnboardingTopic | null = topicsRemaining[topicIdx] ?? null;

  function advanceTitle() {
    if (titleIdx + 1 < titlesRemaining.length) {
      setTitleIdx((i) => i + 1);
    } else if (topicsRemaining.length > 0) {
      setPhase("topics");
      setTitleIdx(0);
    } else {
      complete.mutate();
    }
  }

  function advanceTopic() {
    if (topicIdx + 1 < topicsRemaining.length) {
      setTopicIdx((i) => i + 1);
    } else {
      complete.mutate();
    }
  }

  return (
    <div className="min-h-screen px-10 py-8 max-w-[1100px] mx-auto">
      <button onClick={() => nav("/profiles")} className="pill-ghost mb-6">
        <ArrowLeft size={14} /> Back to profiles
      </button>

      {phase === "titles" && currentTitle && (
        <TitleStep
          title={currentTitle}
          progress={titleProgress}
          phaseLabel="Phase I — The reels you've seen"
          onAnswer={(answer) => {
            rateTitle.mutate(
              {
                title_id: currentTitle.id,
                seen: answer.seen,
                rating: answer.seen ? answer.rating : null,
              },
              { onSuccess: () => advanceTitle() },
            );
          }}
          submitting={rateTitle.isPending}
        />
      )}

      {phase === "topics" && currentTopic && (
        <TopicStep
          topic={currentTopic}
          progress={topicProgress}
          phaseLabel="Phase II — Subjects that pull you in"
          onAnswer={(rating) => {
            rateTopic.mutate(
              { topic: currentTopic.key, rating },
              { onSuccess: () => advanceTopic() },
            );
          }}
          submitting={rateTopic.isPending}
        />
      )}

      {phase === "topics" && !currentTopic && (
        <button className="pill" onClick={() => complete.mutate()} disabled={complete.isPending}>
          Finish onboarding
        </button>
      )}

      {phase === "done" && (
        <DoneStep result={doneResult} onBack={() => nav("/profiles")} />
      )}
    </div>
  );
}

function ProgressHeader({ phaseLabel, progress }: { phaseLabel: string; progress: { done: number; total: number } }): JSX.Element {
  const pct = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
  return (
    <div className="mb-8">
      <div className="section-label">{phaseLabel}</div>
      <div className="flex items-baseline justify-between mt-1">
        <div className="text-parchment text-sm">
          {progress.done} of {progress.total} rated
        </div>
        <div className="font-display text-goldHi text-sm tabular-nums">{pct}%</div>
      </div>
      <div className="h-[2px] mt-3 bg-hairline relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gold"
          style={{ width: `${pct}%`, transition: "width 240ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}
        />
      </div>
    </div>
  );
}

type TitleAnswer = { seen: false; rating: null } | { seen: true; rating: number };

function TitleStep({
  title,
  progress,
  phaseLabel,
  onAnswer,
  submitting,
}: {
  title: OnboardingTitle;
  progress: { done: number; total: number };
  phaseLabel: string;
  onAnswer: (a: TitleAnswer) => void;
  submitting: boolean;
}): JSX.Element {
  const [stage, setStage] = useState<"seen" | "rate">("seen");
  return (
    <div>
      <ProgressHeader phaseLabel={phaseLabel} progress={progress} />

      <div className="card p-12">
        <div className="text-parchment text-sm tracking-smallcaps uppercase font-display">
          {title.kind === "movie" ? "Film" : "Television"} · {title.year ?? "—"}
        </div>
        <h1 className="hero-title mt-2">{title.title}</h1>

        {stage === "seen" && (
          <div className="mt-12">
            <div className="section-label mb-4">Have you seen it?</div>
            <div className="flex gap-3 flex-wrap">
              <button className="pill" onClick={() => onAnswer({ seen: false, rating: null })} disabled={submitting}>
                Skip
              </button>
              <button className="pill" onClick={() => onAnswer({ seen: false, rating: null })} disabled={submitting}>
                No
              </button>
              <button className="pill" aria-pressed onClick={() => setStage("rate")} disabled={submitting}>
                Yes
              </button>
            </div>
          </div>
        )}

        {stage === "rate" && (
          <div className="mt-12">
            <div className="section-label mb-4">Rate it — no fence-sitting</div>
            <RatingRow
              onPick={(rating) => onAnswer({ seen: true, rating })}
              disabled={submitting}
            />
            <button
              className="pill-ghost mt-6"
              onClick={() => setStage("seen")}
              disabled={submitting}
            >
              <ArrowLeft size={14} /> wait, I haven't seen it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TopicStep({
  topic,
  progress,
  phaseLabel,
  onAnswer,
  submitting,
}: {
  topic: OnboardingTopic;
  progress: { done: number; total: number };
  phaseLabel: string;
  onAnswer: (rating: number) => void;
  submitting: boolean;
}): JSX.Element {
  return (
    <div>
      <ProgressHeader phaseLabel={phaseLabel} progress={progress} />
      <div className="card p-12">
        <div className="text-parchment text-sm tracking-smallcaps uppercase font-display">Topic</div>
        <h1 className="hero-title mt-2">{topic.label}</h1>
        <div className="section-label mt-12 mb-4">How much does this pull you in?</div>
        <RatingRow onPick={onAnswer} disabled={submitting} />
        <div className="mt-6 text-parchment text-xs">1 = avoid · 6 = mild · 8 = strong · 10 = obsessed (no 7s)</div>
      </div>
    </div>
  );
}

function RatingRow({ onPick, disabled }: { onPick: (rating: number) => void; disabled: boolean }): JSX.Element {
  return (
    <div className="flex gap-2 flex-wrap">
      {RATING_OPTIONS.map((n) => {
        const blocked = n === 7;
        return (
          <button
            key={n}
            onClick={() => onPick(n)}
            disabled={disabled || blocked}
            title={blocked ? "no fence-sitting" : undefined}
            className={clsx(
              "w-12 h-12 rounded-full font-display text-lg border transition-all duration-200 ease-spring",
              blocked && "opacity-25 line-through cursor-not-allowed border-hairline text-parchment",
              !blocked && "border-gold text-ivory bg-card hover:bg-vermilion hover:border-goldHi hover:scale-105",
            )}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

function DoneStep({ result, onBack }: { result: { tag_weights: Record<string, number>; n_titles_rated: number; n_topics_rated: number } | null; onBack: () => void }): JSX.Element {
  // sort tag_weights for display
  const sorted = result ? Object.entries(result.tag_weights).sort(([, a], [, b]) => b - a) : [];
  const top = sorted.slice(0, 12);
  const bottom = sorted.slice(-6).reverse();

  return (
    <div className="card p-12">
      <div className="forecourt-mark w-16 h-16 mb-6"><Check size={32} /></div>
      <div className="section-label">Onboarding complete</div>
      <h1 className="hero-title mt-2">The algorithm has its bearings.</h1>
      <div className="hero-underline mt-4 w-72 max-w-full" />
      {result && (
        <p className="text-parchment mt-6 max-w-[60ch]">
          {result.n_titles_rated} {result.n_titles_rated === 1 ? "film" : "films"} rated ·{" "}
          {result.n_topics_rated} topic signals · {sorted.length} tag dimensions seeded.
        </p>
      )}

      {result && top.length > 0 && (
        <div className="mt-10 grid grid-cols-2 gap-10">
          <div>
            <div className="section-label mb-3">Pulled toward</div>
            <ul className="space-y-1">
              {top.map(([tag, w]) => (
                <li key={tag} className="flex items-center justify-between text-sm">
                  <span className="text-ivory font-mono">{tag}</span>
                  <span className="text-goldHi tabular-nums">{w > 0 ? "+" : ""}{w.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="section-label mb-3">Pulled away</div>
            <ul className="space-y-1">
              {bottom.map(([tag, w]) => (
                <li key={tag} className="flex items-center justify-between text-sm">
                  <span className="text-ivory font-mono">{tag}</span>
                  <span className="text-vermilionHi tabular-nums">{w.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <button className="pill mt-10" onClick={onBack}>
        Done — return to profiles
      </button>
    </div>
  );
}
